import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { endLocalAccountRoomAdmission } from "../room/room-admission-lifecycle";
import { authClient, authResponse } from "./client";
import { configureAuthenticationRecoveryHandler } from "./authentication-request-transport";

const sessionKey = ["auth", "session"] as const;
const guestStorageKey = "puzzle-muzzle-guest-mode";
const sessionSchema = z
  .object({
    user: z.object({
      id: z.string(),
      email: z.string(),
      name: z.string(),
      emailVerified: z.boolean(),
    }),
  })
  .nullable();
export type AuthUser = NonNullable<z.infer<typeof sessionSchema>>["user"];
export interface AuthState {
  user: AuthUser | null;
  status: "loading" | "signed-in" | "signed-out" | "guest" | "unavailable";
  continueAsGuest(): void;
  exitGuest(): void;
  refreshSession(): Promise<void>;
  recoverRejectedSession(): Promise<void>;
  signOut(): Promise<void>;
}
export const AuthContext = createContext<AuthState>(null!);

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [guest, setGuest] = useState(
    () => sessionStorage.getItem(guestStorageKey) === "true",
  );
  const session = useQuery({
    queryKey: sessionKey,
    queryFn: async ({ signal }) =>
      sessionSchema.parse(
        authResponse(await authClient.session.mutate({}, { signal })),
      ),
    staleTime: 30_000,
    retry: false,
  });
  const user = session.data?.user ?? null;
  const previousUserId = useRef(user?.id);
  const channel = useRef<BroadcastChannel | null>(null);
  const recovery = useRef<Promise<void> | null>(null);
  const exitGuest = useCallback(() => {
    sessionStorage.removeItem(guestStorageKey);
    setGuest(false);
  }, []);
  const continueAsGuest = useCallback(() => {
    sessionStorage.setItem(guestStorageKey, "true");
    setGuest(true);
  }, []);
  const refreshSession = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: sessionKey });
  }, [queryClient]);
  const clearSession = useCallback(async () => {
    await queryClient.cancelQueries({ queryKey: ["auth"] });
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] !== "auth" || query.queryKey[1] !== "session",
    });
    previousUserId.current = undefined;
    queryClient.setQueryData(sessionKey, null);
    exitGuest();
    endLocalAccountRoomAdmission();
  }, [exitGuest, queryClient]);
  const signOut = useCallback(async () => {
    authResponse(await authClient.signOut.mutate({}));
    await clearSession();
    channel.current?.postMessage("session-changed");
  }, [clearSession]);
  const recoverRejectedSession = useCallback(() => {
    if (recovery.current) return recovery.current;
    recovery.current = (async () => {
      try {
        authResponse(await authClient.signOut.mutate({}));
      } catch {
        /* The API has already rejected this session; local recovery still applies. */
      } finally {
        await clearSession();
        channel.current?.postMessage("session-changed");
      }
    })().finally(() => {
      recovery.current = null;
    });
    return recovery.current;
  }, [clearSession]);

  useEffect(
    () => configureAuthenticationRecoveryHandler(recoverRejectedSession),
    [recoverRejectedSession],
  );
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const connection = new BroadcastChannel("puzzleini-auth");
    channel.current = connection;
    connection.onmessage = () => {
      void refreshSession();
    };
    return () => {
      channel.current = null;
      connection.close();
    };
  }, [refreshSession]);
  useEffect(() => {
    if (user) exitGuest();
    if (previousUserId.current !== user?.id) {
      const previous = previousUserId.current;
      if (previous) {
        queryClient.removeQueries({
          predicate: (query) => query.queryKey.includes(previous),
        });
        endLocalAccountRoomAdmission();
      }
      previousUserId.current = user?.id;
    }
  }, [exitGuest, queryClient, user?.id]);
  const status: AuthState["status"] = session.isPending
    ? "loading"
    : user
      ? "signed-in"
      : session.isError
        ? "unavailable"
        : guest
          ? "guest"
          : "signed-out";
  const value = useMemo(
    () => ({
      user,
      status,
      continueAsGuest,
      exitGuest,
      refreshSession,
      recoverRejectedSession,
      signOut,
    }),
    [
      user,
      status,
      continueAsGuest,
      exitGuest,
      refreshSession,
      recoverRejectedSession,
      signOut,
    ],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}
